import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import {
  HOLD_CATEGORY_LABELS,
  HOLD_DOCUMENT_TYPE_LABELS,
  holdDurationDays,
  validateHoldReason,
  type DocumentHold,
  type HoldCategory,
  type HoldDocumentSummary,
  type HoldDocumentType
} from "../../domain/review/document-hold.js";

/**
 * Peças de interface do HOLD, compartilhadas por Checklist/Inspeção e pelos
 * relatórios END: botão [DEIXAR EM HOLD] / [TIRAR DE HOLD], modal "COLOCAR EM
 * HOLD" (motivo obrigatório + categoria), modal "TIRAR DOCUMENTO DE HOLD?" e
 * o banner com MOTIVO DO HOLD + COMENTÁRIOS DA ENGENHARIA. A regra fica no
 * DocumentHoldService; aqui só se renderiza o estado que ele devolve.
 */

function esc(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
}

function ctx(clientId?: string | null): any {
  const c = contextStore.get();
  return { activeClientId: clientId || c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
}

function holdService(): any {
  return (window as any).app?.platform?.documentHoldService;
}

export interface HoldDocumentInfo {
  documentType: HoldDocumentType;
  documentId: string;
  summary: HoldDocumentSummary;
  currentStatus: { status: string; label: string };
}

/** Botão da barra de ações: DEIXAR EM HOLD (sem HOLD ativo) ou TIRAR DE HOLD (com HOLD ativo). */
export function holdButton(hold: DocumentHold | null, editable: boolean, buttonClass = "btn"): string {
  if (hold) {
    return contextStore.hasPermission(PermissionCode.ReportRemoveFromHold) ? `<button class="${buttonClass} hold-release-btn" id="btn-release-hold">TIRAR DE HOLD</button>` : "";
  }
  if (!editable || !contextStore.hasPermission(PermissionCode.ReportPutOnHold)) return "";
  return `<button class="${buttonClass} hold-btn" id="btn-put-on-hold">DEIXAR EM HOLD</button>`;
}

/** Banner do documento EM HOLD: motivo, quem, quando, tempo em HOLD e comentários da Engenharia. */
export function holdBanner(hold: DocumentHold | null, highlight = false): string {
  if (!hold) return "";
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");
  const days = holdDurationDays(hold);
  return `
    <div class="hold-banner ${highlight ? "deep-link-highlight" : ""}" data-section="hold" data-item="${esc(hold.id)}">
      <div class="hold-banner-head"><strong>EM HOLD</strong><span>${days === 0 ? "hoje" : `há ${days} dia${days === 1 ? "" : "s"}`} · status anterior: ${esc(hold.previousStatusLabel)}</span></div>
      <div class="plh-banner-grid">
        <span><b>MOTIVO DO HOLD:</b> ${esc(hold.reason)}</span>
        <span><b>Categoria:</b> ${esc(hold.category ? HOLD_CATEGORY_LABELS[hold.category] : "—")}</span>
        <span><b>Colocado por:</b> ${esc(hold.placedBy.name)}</span>
        <span><b>Data/hora:</b> ${esc(when(hold.placedAt))}</span>
      </div>
      ${hold.engineeringComments.length > 0 ? `
        <div class="hold-comments"><b>COMENTÁRIOS DA ENGENHARIA</b>
          ${hold.engineeringComments.map(c => `<div class="hold-comment"><span class="hold-comment-meta">${esc(c.by.name)} · ${esc(when(c.at))}</span><span>${esc(c.comment)}</span></div>`).join("")}
        </div>` : `<small>Sem comentário da Engenharia até o momento. A edição fica bloqueada até "TIRAR DE HOLD".</small>`}
    </div>`;
}

/** <app-hold-modal> — COLOCAR EM HOLD: motivo obrigatório, categoria opcional. */
export class HoldModal extends HTMLElement {
  info: HoldDocumentInfo | null = null;
  onDone: ((hold: DocumentHold) => void) | null = null;
  onClosed: (() => void) | null = null;
  private reason = "";
  private category: HoldCategory | "" = "";
  private errors: string[] = [];
  private busy = false;

  connectedCallback() { this.render(); }

  private close() { this.remove(); this.onClosed?.(); }

  private async confirm() {
    if (this.busy || !this.info) return;
    this.errors = validateHoldReason(this.reason);
    if (this.errors.length > 0) { this.render(); return; }
    this.busy = true; this.render();
    try {
      const hold = await holdService().putOnHold(ctx(this.info.summary.clientId), { documentType: this.info.documentType, documentId: this.info.documentId, summary: this.info.summary, currentStatus: this.info.currentStatus, reason: this.reason, category: this.category || null });
      this.remove();
      this.onDone?.(hold);
      this.onClosed?.();
    } catch (e: any) {
      this.errors = [e?.safeMessage || e?.message || "Erro ao colocar em HOLD."];
      this.busy = false;
      this.render();
    }
  }

  render() {
    const info = this.info;
    if (!info) { this.innerHTML = ""; return; }
    this.innerHTML = `
      <div class="plh-modal-backdrop">
        <div class="plh-modal" style="max-width:640px;">
          <div class="plh-modal-head"><span>COLOCAR EM HOLD — ${esc(HOLD_DOCUMENT_TYPE_LABELS[info.documentType])} ${esc(info.summary.documentNumber)}</span><button class="plh-icon-btn" data-act="close">✕</button></div>
          <div class="plh-modal-body">
            <div class="plh-note">O documento para de ser editado, aparece em ENGENHARIA → RELATÓRIOS EM HOLD e volta ao status atual (<b>${esc(info.currentStatus.label)}</b>) quando for liberado.</div>
            <label class="plh-field"><span>MOTIVO DO HOLD *</span><textarea class="plh-ctl" id="hold-reason" rows="4" placeholder="Descreva por que o documento precisa ficar em HOLD.">${esc(this.reason)}</textarea></label>
            <label class="plh-field"><span>CATEGORIA (opcional)</span>
              <select class="plh-ctl" id="hold-category"><option value="">—</option>${(Object.keys(HOLD_CATEGORY_LABELS) as HoldCategory[]).map(k => `<option value="${k}" ${this.category === k ? "selected" : ""}>${esc(HOLD_CATEGORY_LABELS[k])}</option>`).join("")}</select></label>
            ${this.errors.length > 0 ? `<div class="plh-errors">${this.errors.map(e => `<div>${esc(e)}</div>`).join("")}</div>` : ""}
            <div class="plh-actions">
              <button class="plh-btn" data-act="close" ${this.busy ? "disabled" : ""}>CANCELAR</button>
              <button class="plh-btn primary hold-confirm" data-act="confirm" ${this.busy ? "disabled" : ""}>CONFIRMAR HOLD</button>
            </div>
          </div>
        </div>
      </div>`;
    this.querySelectorAll("[data-act='close']").forEach(b => b.addEventListener("click", () => this.close()));
    this.querySelector("[data-act='confirm']")?.addEventListener("click", () => this.confirm());
    this.querySelector("#hold-reason")?.addEventListener("input", e => { this.reason = (e.target as HTMLTextAreaElement).value; });
    this.querySelector("#hold-category")?.addEventListener("change", e => { this.category = (e.target as HTMLSelectElement).value as HoldCategory | ""; });
  }
}

/** <app-hold-release-modal> — TIRAR DOCUMENTO DE HOLD? (observação de liberação opcional). */
export class HoldReleaseModal extends HTMLElement {
  hold: DocumentHold | null = null;
  currentStatus: { status: string; label: string } | null = null;
  onDone: ((hold: DocumentHold) => void) | null = null;
  onClosed: (() => void) | null = null;
  private note = "";
  private errors: string[] = [];
  private busy = false;

  connectedCallback() { this.render(); }

  private close() { this.remove(); this.onClosed?.(); }

  private async confirm() {
    if (this.busy || !this.hold) return;
    this.busy = true; this.render();
    try {
      const released = await holdService().release(ctx(this.hold.summary.clientId), this.hold.id, this.note, this.currentStatus || undefined);
      this.remove();
      this.onDone?.(released);
      this.onClosed?.();
    } catch (e: any) {
      this.errors = [e?.safeMessage || e?.message || "Erro ao tirar de HOLD."];
      this.busy = false;
      this.render();
    }
  }

  render() {
    const h = this.hold;
    if (!h) { this.innerHTML = ""; return; }
    this.innerHTML = `
      <div class="plh-modal-backdrop">
        <div class="plh-modal" style="max-width:600px;">
          <div class="plh-modal-head"><span>TIRAR DOCUMENTO DE HOLD?</span><button class="plh-icon-btn" data-act="close">✕</button></div>
          <div class="plh-modal-body">
            <div class="plh-note"><b>${esc(h.summary.documentNumber)}</b> · Motivo do HOLD: ${esc(h.reason)}<br>O status de workflow anterior será restaurado: <b>${esc(this.currentStatus?.label || h.previousStatusLabel)}</b>.</div>
            <label class="plh-field"><span>OBSERVAÇÃO DE LIBERAÇÃO (opcional)</span><textarea class="plh-ctl" id="hold-note" rows="3">${esc(this.note)}</textarea></label>
            ${this.errors.length > 0 ? `<div class="plh-errors">${this.errors.map(e => `<div>${esc(e)}</div>`).join("")}</div>` : ""}
            <div class="plh-actions">
              <button class="plh-btn" data-act="close" ${this.busy ? "disabled" : ""}>CANCELAR</button>
              <button class="plh-btn primary" data-act="confirm" ${this.busy ? "disabled" : ""}>TIRAR DE HOLD</button>
            </div>
          </div>
        </div>
      </div>`;
    this.querySelectorAll("[data-act='close']").forEach(b => b.addEventListener("click", () => this.close()));
    this.querySelector("[data-act='confirm']")?.addEventListener("click", () => this.confirm());
    this.querySelector("#hold-note")?.addEventListener("input", e => { this.note = (e.target as HTMLTextAreaElement).value; });
  }
}

if (!customElements.get("app-hold-modal")) customElements.define("app-hold-modal", HoldModal);
if (!customElements.get("app-hold-release-modal")) customElements.define("app-hold-release-modal", HoldReleaseModal);

export function openHoldModal(info: HoldDocumentInfo, onDone: (hold: DocumentHold) => void, onClosed?: () => void): HoldModal {
  const modal = document.createElement("app-hold-modal") as HoldModal;
  modal.info = info; modal.onDone = onDone; modal.onClosed = onClosed || null;
  document.body.appendChild(modal);
  return modal;
}

export function openHoldReleaseModal(hold: DocumentHold, currentStatus: { status: string; label: string }, onDone: (hold: DocumentHold) => void, onClosed?: () => void): HoldReleaseModal {
  const modal = document.createElement("app-hold-release-modal") as HoldReleaseModal;
  modal.hold = hold; modal.currentStatus = currentStatus; modal.onDone = onDone; modal.onClosed = onClosed || null;
  document.body.appendChild(modal);
  return modal;
}

/** HOLD ativo do documento (ou nulo) — consulta ao serviço central. */
export async function loadActiveHold(documentType: HoldDocumentType, documentId: string, clientId?: string | null): Promise<DocumentHold | null> {
  try {
    const svc = holdService();
    return svc ? await svc.activeHold(ctx(clientId), documentType, documentId) : null;
  } catch {
    return null;
  }
}

export const HOLD_UI_STYLES = `
  .hold-btn { background:#b45309 !important; border-color:#b45309 !important; color:#fff !important; }
  .hold-release-btn { background:#0f766e !important; border-color:#0f766e !important; color:#fff !important; }
  .hold-confirm { background:#b45309 !important; border-color:#b45309 !important; }
  .hold-banner { padding:12px 14px; border-radius:8px; border:1px solid rgba(180,83,9,0.7); background: rgba(180,83,9,0.14); color: var(--ia-warning-fg, #fde68a); font-size:0.82rem; display:flex; flex-direction:column; gap:8px; margin-bottom:12px; }
  .hold-banner-head { display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap; }
  .hold-banner-head strong { font-size:0.9rem; letter-spacing:0.04em; }
  .hold-comments { display:flex; flex-direction:column; gap:6px; padding-top:6px; border-top:1px dashed rgba(253,230,138,0.35); }
  .hold-comment { display:flex; flex-direction:column; gap:2px; background: rgba(0,0,0,0.2); border-radius:6px; padding:6px 8px; }
  .hold-comment-meta { font-size:0.7rem; opacity:0.8; }
`;
